import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  Button,
  Card,
  TablePagination,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Container,
  Stack,
  CardContent,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { DataGrid } from "@mui/x-data-grid";
import Error403 from "../Error/403";
import AssingnRole from "./AssingnRole";
import Userlabs from "./Userlabs1";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

export default function Request() {
  const classes = useStyles();

  const [state, setState] = React.useState({
    email: "",
    username: "",
    groups: "",
    password: "",
    password2: "",
    errorMessage: "",
    right: false,
    radioList: "A",
    delete_id: "",
    active: "1",
  });

  const [userlist, setusers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/user_list/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data.detail) {
          localStorage.setItem("user_access", data.detail);
        } else {
          const filteredCustomers = data.filter((item) => item.groups.includes(4));
          setusers(filteredCustomers);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setLoading(false));
  }, []);

  const activate = (userId) => () => {
    const activatePayload = {
      username: state.username,
      password: state.password,
      groups: state.groups.toString(),
      active: state.active,
    };
    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/user/${userId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify(activatePayload),
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error(error);
        window.location.reload();
      });
  };

  const [value, setValue] = React.useState(0);
  const [openactive, setOpenactive] = React.useState(false);
  const [opencontact, setOpencontact] = React.useState(false);

  const handleCloseActive = () => {
    setOpenactive(false);
  };

  const handleCloseContact = () => {
    setOpencontact(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const filteredUserList = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    return (userlist || []).filter((instance) => {
      if (statusFilter === "active" && !instance.is_active) {
        return false;
      }
      if (statusFilter === "inactive" && instance.is_active) {
        return false;
      }
      if (!normalizedTerm) {
        return true;
      }
      return (
        instance.email?.toLowerCase().includes(normalizedTerm) ||
        instance.username?.toLowerCase().includes(normalizedTerm)
      );
    });
  }, [userlist, searchTerm, statusFilter]);

  const paginatedUserList = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUserList.slice(start, start + rowsPerPage);
  }, [filteredUserList, page, rowsPerPage]);

  const summaryMetrics = useMemo(() => {
    const total = userlist.length;
    const active = userlist.filter((user) => user.is_active).length;
    const inactive = total - active;
    const inView = filteredUserList.length;
    return { total, active, inactive, inView };
  }, [userlist, filteredUserList]);

  const renderLoadingSkeleton = () => (
    <GridSkeleton />
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        py: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        color: "text.secondary",
      }}
    >
      <HourglassBottomIcon sx={{ fontSize: 56, mb: 1 }} />
      <Typography variant="h6" gutterBottom>
        {userlist.length === 0 ? "No trainers available" : "No trainers match your filters"}
      </Typography>
      <Typography variant="body2" sx={{ maxWidth: 360 }}>
        Try adjusting your search or filter criteria to find the trainer you need.
      </Typography>
    </Box>
  );

  const renderDataGrid = () => {
    if (loading) {
      return renderLoadingSkeleton();
    }

    if (paginatedUserList.length === 0) {
      return renderEmptyState();
    }

    const rows = paginatedUserList.filter(
      (customer) => customer.is_active === true || String(customer.is_active).toLowerCase() === "true"
    );

    return (
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <DataGrid
          rows={rows}
          getRowId={(row) => row.id}
          density="standard"
          autoHeight
          disableSelectionOnClick
          columns={[
            {
              field: "username",
              headerName: "Name",
              flex: 1,
              minWidth: 160,
              headerAlign: "left",
              align: "left",
            },
            {
              field: "email",
              headerName: "Email",
              flex: 1.4,
              minWidth: 220,
              headerAlign: "left",
              align: "left",
            },
            {
              field: "assignLab",
              headerName: "Assign Lab",
              flex: 1,
              minWidth: 160,
              headerAlign: "center",
              align: "center",
              renderCell: (params) => (
                <Tooltip title="Assign or update labs">
                  <span>
                    <AssingnRole groups={params.row.groups} user_id={params.row.id} />
                  </span>
                </Tooltip>
              ),
            },
            {
              field: "details",
              headerName: "Details",
              flex: 1,
              minWidth: 160,
              headerAlign: "center",
              align: "center",
              renderCell: (params) => (
                <Tooltip title="View assigned labs">
                  <span>
                    <Userlabs groups={params.row.groups} user_id={params.row.id} />
                  </span>
                </Tooltip>
              ),
            },
          ]}
          sx={{
            border: "none",
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.action.hover,
              borderBottom: '1px solid',
              borderColor: 'divider',
              minHeight: 56,
              maxHeight: 56,
              fontWeight: 600,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'divider',
            },
          }}
          hideFooter
        />
        <Box sx={{ display: "flex", justifyContent: "flex-start", pl: 1, pt: 1 }}>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredUserList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                pl: 0,
              },
              '& .MuiTablePagination-spacer': {
                display: 'none',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                mx: 0.5,
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  if (
    localStorage.getItem("user_access") ===
      "You do not have permission to perform this action." &&
    localStorage.getItem("group") !== "admin"
  ) {
    return <Error403 />;
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>LabAsService</title>
      </Helmet>
      <Container maxWidth="xl" className={classes.root} sx={{ py: { xs: 3, md: 4 } }}>
        <TabPanel value={value} index={0}>
          <Stack spacing={3}>
            <Card
              sx={{
                borderRadius: 3,
                p: { xs: 2.5, md: 3 },
                background: 'linear-gradient(135deg, rgba(25,118,210,0.12), rgba(46,125,50,0.1))',
              }}
            >
              <GridHeader summary={summaryMetrics} />
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="flex-start"
                  sx={{ flexWrap: 'nowrap' }}
                >
                  <ToggleButtonGroup
                    value={statusFilter}
                    exclusive
                    onChange={(event, value) => value && setStatusFilter(value)}
                    size="small"
                    sx={{
                      flexWrap: 'nowrap',
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="active">Active</ToggleButton>
                    <ToggleButton value="inactive">Inactive</ToggleButton>
                  </ToggleButtonGroup>

                  <TextField
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setPage(0);
                    }}
                    placeholder="Search by name or email"
                    size="small"
                    sx={{ width: { xs: 220, md: 260 }, maxWidth: '100%' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <Select
                      value={rowsPerPage}
                      onChange={handleChangeRowsPerPage}
                      displayEmpty
                      renderValue={(value) => `Rows: ${value}`}
                    >
                      {[10, 25, 50, 75, 100].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option} rows
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Box>{renderDataGrid()}</Box>
              </CardContent>
            </Card>

            <ActivationDialogs
              userlist={userlist}
              state={state}
              openactive={openactive}
              opencontact={opencontact}
              handleCloseActive={handleCloseActive}
              handleCloseContact={handleCloseContact}
              activate={activate}
            />
          </Stack>
        </TabPanel>
      </Container>
    </React.Fragment>
  );
}

const GridSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    <Stack spacing={1.5}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} variant="rounded" height={48} animation="wave" />
      ))}
    </Stack>
  </Box>
);

const GridHeader = ({ summary }) => (
  <Stack spacing={2}>
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Assign Trainers
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Manage trainer assignments, activate accounts, and review lab ownership at a glance.
      </Typography>
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <MetricCard
        icon={<GroupIcon color="primary" />}
        label="Total trainers"
        value={summary.total}
        chipLabel={`${summary.inView} in view`}
      />
      <MetricCard
        icon={<CheckCircleIcon color="success" />}
        label="Active"
        value={summary.active}
        chipLabel="Active accounts"
        chipColor="success"
      />
      <MetricCard
        icon={<HourglassBottomIcon color="warning" />}
        label="Inactive"
        value={summary.inactive}
        chipLabel="Needs action"
        chipColor="warning"
      />
    </Stack>
  </Stack>
);

const MetricCard = ({ icon, label, value, chipLabel, chipColor = "default" }) => (
  <Card elevation={0} sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 180 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      {icon}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={600}>
          {value}
        </Typography>
        <Chip size="small" label={chipLabel} color={chipColor} sx={{ mt: 0.5 }} />
      </Box>
    </Stack>
  </Card>
);

MetricCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  chipLabel: PropTypes.string,
  chipColor: PropTypes.string,
};

const ActivationDialogs = ({
  userlist,
  state,
  openactive,
  opencontact,
  handleCloseActive,
  handleCloseContact,
  activate,
}) => (
  <>
    <Dialog
      open={openactive}
      onClose={handleCloseActive}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {userlist.map((user) => (
        <React.Fragment key={user.id}>
          {user.email === state.email ? (
            <>
              <DialogTitle id="alert-dialog-title">
                User already exists and is inactive. Activate account for
                <strong style={{ color: "#EF5350", marginLeft: 4 }}>{state.email}</strong>?
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description" />
              </DialogContent>
              <DialogActions>
                <Tooltip title="Cancel" arrow>
                  <Button onClick={handleCloseActive} color="primary">
                    Cancel
                  </Button>
                </Tooltip>
                <Tooltip title="Activate" arrow>
                  <Button onClick={activate(user.id)} color="primary" autoFocus>
                    Activate
                  </Button>
                </Tooltip>
              </DialogActions>
            </>
          ) : null}
        </React.Fragment>
      ))}
    </Dialog>

    <Dialog
      open={opencontact}
      onClose={handleCloseContact}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        User is inactive. Please contact
        <strong>
          <a href="mailto:support@zippyops.com" style={{ marginLeft: 5, color: "#2096ff" }}>
            support@zippyops.com
          </a>
        </strong>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" />
      </DialogContent>
      <DialogActions>
        <Tooltip title="Close" arrow>
          <Button onClick={handleCloseContact} color="primary">
            Close
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  </>
);

ActivationDialogs.propTypes = {
  userlist: PropTypes.array.isRequired,
  state: PropTypes.object.isRequired,
  openactive: PropTypes.bool.isRequired,
  opencontact: PropTypes.bool.isRequired,
  handleCloseActive: PropTypes.func.isRequired,
  handleCloseContact: PropTypes.func.isRequired,
  activate: PropTypes.func.isRequired,
};